import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { BDC_INVOICE_DEFAULT_COPIES, listBdcDocuments, renderBdcDocuments } from "../../../services";
import { saveBlobResponse } from "../../../utils/downloadBlob";
import { BdcDocumentFormat, BdcDocumentShipment, BdcDocumentType } from "../../../types/bdc.type";
import BranchChip from "../Branch/BranchChip";
import BranchSelector from "../Branch/BranchSelector";
import { branchCodeOf } from "../Branch/branches";
import TestModeBanner from "../Branch/TestModeBanner";
import { useBdcBranchSelection, useBdcBranches } from "../Branch/useBdcBranches";
import { pcs, roundLabel, rowsOf, thaiDate, todayBkk } from "../format";
import InvoiceCopiesField from "../Invoice/InvoiceCopiesField";
import { openPdfResponse } from "../Invoice/pdf";
import StatusChip from "../StatusChip";
import { CARD_SX, TABLE_SX } from "../styles";
import Tag from "../Tag";
import { useBdcDcs } from "../useBdcDcs";
import {
  BDC_DOCUMENTS,
  documentLabel,
  outputProblem,
  pageCount,
  parseLineNos,
  parseSelectParam,
  planDocuments,
  sortDocumentShipments,
  toggleDocument,
} from "./documents";

type DateMode = "order" | "delivery";
type Output = "print" | "pdf" | "xlsx";
const ALL = "all";

const CHECK_SX = { py: 0.25, "& .MuiFormControlLabel-label": { fontSize: "0.8125rem" } } as const;

/**
 * BDC · ศูนย์เอกสาร (W9) — tick shipments of one day × documents, get one merged PDF (to print or
 * keep) or the Excel of one sheet document. Each shipment offers only what it can print now
 * (`available_docs`): the INV only once issued, the per-store sheets only with a PICKLST.
 */
export default function BdcDocuments() {
  const [params, setParams] = useSearchParams();
  const { branches, ready: branchesReady } = useBdcBranches();
  const branch = useBdcBranchSelection(branches);
  const { dcs } = useBdcDcs();

  const mode: DateMode = params.get("delivery_date") ? "delivery" : "order";
  const date = params.get(mode === "order" ? "order_date" : "delivery_date") || todayBkk();
  const dcCode = params.get("dc_code") || ALL;

  const [shipments, setShipments] = useState<BdcDocumentShipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(parseSelectParam(params.get("select"))));
  const [docs, setDocs] = useState<Set<BdcDocumentType>>(() => new Set<BdcDocumentType>(["route_summary"]));
  const [busy, setBusy] = useState<Output | null>(null);
  const [copies, setCopies] = useState(BDC_INVOICE_DEFAULT_COPIES);
  const [lineText, setLineText] = useState("");

  const load = useCallback(async () => {
    if (!branchesReady) return;
    setLoading(true);
    try {
      const {
        data: { data },
      } = await listBdcDocuments({
        ...(mode === "order" ? { order_date: date } : { delivery_date: date }),
        ...(dcCode !== ALL ? { dc_code: dcCode } : {}),
        ...(branch.companyId ? { company_id: branch.companyId } : {}),
      });
      const rows = sortDocumentShipments(rowsOf(data));
      setShipments(rows);
      // keep only ticks that are still on the list
      setSelected((current) => new Set(rows.map((r) => r.shipment_uuid).filter((uuid) => current.has(uuid))));
    } catch {
      setShipments([]);
    } finally {
      setLoading(false);
    }
  }, [mode, date, dcCode, branch.companyId, branchesReady]);

  useEffect(() => {
    load();
  }, [load]);

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    next.delete("select");
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === ALL) next.delete(key);
      else next.set(key, value);
    });
    setParams(next, { replace: true });
  };

  const handleMode = (event: ChangeEvent<HTMLInputElement>) => {
    const nextMode = event.target.value as DateMode;
    update(nextMode === "order" ? { order_date: date, delivery_date: null } : { delivery_date: date, order_date: null });
  };
  const handleDate = (value: Dayjs | null) => {
    if (!value || !value.isValid()) return;
    update({ [mode === "order" ? "order_date" : "delivery_date"]: value.format("YYYY-MM-DD") });
  };

  const groups = useMemo(() => {
    const byBranch = new Map<string, BdcDocumentShipment[]>();
    shipments.forEach((s) => {
      const list = byBranch.get(s.company_id) ?? [];
      list.push(s);
      byBranch.set(s.company_id, list);
    });
    return Array.from(byBranch.entries());
  }, [shipments]);

  const plan = useMemo(() => planDocuments(shipments, selected, docs), [shipments, selected, docs]);
  const isSlip = docs.has("store_slip");
  const lineNos = isSlip ? parseLineNos(lineText) : [];
  const lineProblem = lineNos === null ? "สายต้องเป็นตัวเลข คั่นด้วย , หรือเว้นวรรค" : null;
  const pdfProblem = outputProblem(plan, docs, "pdf") ?? lineProblem;
  const xlsxProblem = outputProblem(plan, docs, "xlsx");

  const toggle = (uuid: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  const toggleGroup = (rows: BdcDocumentShipment[], on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      rows.forEach((r) => (on ? next.add(r.shipment_uuid) : next.delete(r.shipment_uuid)));
      return next;
    });
  const toggleDoc = (type: BdcDocumentType) => setDocs((current) => toggleDocument(current, type));

  const produce = async (output: Output) => {
    const format: BdcDocumentFormat = output === "xlsx" ? "xlsx" : "pdf";
    if (outputProblem(plan, docs, format) || lineNos === null) return;
    setBusy(output);
    try {
      const response = await renderBdcDocuments({
        shipment_uuids: plan.map((entry) => entry.shipment.shipment_uuid),
        docs: BDC_DOCUMENTS.map((d) => d.type).filter((type) => docs.has(type)),
        format,
        ...(docs.has("invoice") ? { copies } : {}),
        ...(isSlip && lineNos.length > 0 ? { line_nos: lineNos } : {}),
      });
      const name = isSlip
        ? `bdc-store-slips-${plan[0]?.shipment.dc_code ?? ""}-${date}.pdf`
        : `bdc-documents-${date}.${format}`;
      if (output === "print") openPdfResponse(response, name);
      else saveBlobResponse(response, name);
    } catch {
      // the interceptor reports it (errorMsgByStatus)
    } finally {
      setBusy(null);
    }
  };

  const dcOptions = branch.companyId ? dcs.filter((dc) => dc.company_id === branch.companyId) : dcs;

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Box mb={2}>
        <Typography variant="h6" fontWeight={700}>
          BDC · ศูนย์เอกสาร · {thaiDate(date)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          เลือกหลาย shipment × หลายเอกสาร → พิมพ์ / PDF ไฟล์เดียว / Excel · ใบคุมใช้ยอดส่ง
        </Typography>
      </Box>

      <Stack gap={2} sx={TABLE_SX}>
        <Card sx={{ ...CARD_SX, p: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
            <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
              <BranchSelector branches={branches} value={branch.selected} onChange={branch.select} />
              <TextField select size="small" label="กรองตาม" value={mode} onChange={handleMode} sx={{ width: 140 }}>
                <MenuItem value="order">วันที่สั่ง</MenuItem>
                <MenuItem value="delivery">วันส่ง</MenuItem>
              </TextField>
              <DatePicker
                label={mode === "order" ? "วันที่สั่ง" : "วันส่ง"}
                value={dayjs(date)}
                format="DD/MM/YYYY"
                onChange={handleDate}
                slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
              />
              <TextField
                select
                size="small"
                label="DC"
                value={dcCode}
                onChange={(e) => update({ dc_code: e.target.value })}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value={ALL}>ทุก DC</MenuItem>
                {dcOptions.map((dc) => (
                  <MenuItem key={dc.dc_code} value={dc.dc_code}>
                    {dc.dc_code} {dc.dc_name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </LocalizationProvider>
        </Card>

        <TestModeBanner branches={branch.viewed} />
        {loading && <LinearProgress />}

        <Stack direction="row" gap={2} alignItems="flex-start" flexWrap="wrap">
          <Card sx={{ ...CARD_SX, p: 1.75, flex: "1 1 280px", minWidth: 0 }} data-testid="bdc-doc-shipments">
            <Typography fontWeight={600} mb={0.75}>
              1 · เลือก shipment
            </Typography>
            {shipments.length === 0 && !loading && <Typography className="sub">ไม่มี shipment ของวันนี้</Typography>}
            {groups.map(([companyId, rows]) => {
              const branchRow = branches.find((b) => b.company_id === companyId);
              const ticked = rows.filter((r) => selected.has(r.shipment_uuid)).length;
              return (
                <Box key={companyId} mb={1}>
                  <FormControlLabel
                    sx={CHECK_SX}
                    control={
                      <Checkbox
                        size="small"
                        checked={ticked === rows.length}
                        indeterminate={ticked > 0 && ticked < rows.length}
                        onChange={(e) => toggleGroup(rows, e.target.checked)}
                      />
                    }
                    label={`ทั้งหมดของสาขา ${branchRow ? branchCodeOf(branchRow) : rows[0].branch_code ?? companyId} (${rows.length})`}
                  />
                  {rows.map((s) => (
                    <Stack key={s.shipment_uuid} direction="row" alignItems="center" gap={0.75} sx={{ pl: 2 }}>
                      <FormControlLabel
                        sx={{ ...CHECK_SX, mr: 0 }}
                        control={<Checkbox size="small" checked={selected.has(s.shipment_uuid)} onChange={() => toggle(s.shipment_uuid)} />}
                        label={`${s.dc_code} ${s.dc_name} · ${roundLabel(s.kind, s.round)}`}
                      />
                      <StatusChip status={s.status} />
                      {s.invoice_no && <span className="sub mono">{s.invoice_no}</span>}
                    </Stack>
                  ))}
                </Box>
              );
            })}
            <Typography className="sub">เรียงตาม WB → รอบ</Typography>
          </Card>

          <Card sx={{ ...CARD_SX, p: 1.75, flex: "1 1 280px", minWidth: 0 }}>
            <Typography fontWeight={600} mb={0.75}>
              2 · เลือกเอกสาร
            </Typography>
            {BDC_DOCUMENTS.map((d) => (
              <Box key={d.type}>
                <FormControlLabel
                  sx={CHECK_SX}
                  control={<Checkbox size="small" checked={docs.has(d.type)} onChange={() => toggleDoc(d.type)} />}
                  label={
                    <>
                      {d.label} <span className="sub">· {d.hint}</span>
                    </>
                  }
                />
              </Box>
            ))}
            {docs.has("invoice") && (
              <Box mt={1}>
                <InvoiceCopiesField value={copies} onChange={setCopies} />
              </Box>
            )}
            {isSlip && (
              <Stack gap={0.75} mt={1}>
                <TextField
                  size="small"
                  label="สาย (ไม่กรอก = ทุกสาย)"
                  placeholder="เช่น 101, 102"
                  value={lineText}
                  onChange={(e) => setLineText(e.target.value)}
                  error={Boolean(lineProblem)}
                  helperText={lineProblem ?? "ติ๊ก shipment ของ DC เดียว · ตอนสั่งพิมพ์เลือกกระดาษ A5 แนวนอน"}
                  sx={{ maxWidth: 260 }}
                  slotProps={{ htmlInput: { "data-testid": "bdc-store-slip-lines" } }}
                />
              </Stack>
            )}
            <Typography fontWeight={600} mt={1.5} mb={0.75}>
              3 · ได้ออกมาเป็น
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Button variant="contained" disabled={Boolean(pdfProblem)} loading={busy === "print"} onClick={() => produce("print")}>
                พิมพ์
              </Button>
              <Button variant="outlined" disabled={Boolean(pdfProblem)} loading={busy === "pdf"} onClick={() => produce("pdf")}>
                PDF ไฟล์เดียว
              </Button>
              <Button variant="outlined" disabled={Boolean(xlsxProblem)} loading={busy === "xlsx"} onClick={() => produce("xlsx")}>
                Excel
              </Button>
            </Stack>
            <Typography className="sub" mt={0.75}>
              {pdfProblem ?? `${pcs(pageCount(plan))} เอกสาร จาก ${pcs(plan.length)} shipment`}
              {!pdfProblem && xlsxProblem && ` · Excel: ${xlsxProblem}`}
            </Typography>
            <Typography className="sub">
              {isSlip
                ? "ลำดับในไฟล์: ทีละ shipment → ทีละสาย → ตามจุดส่ง · เลขหน้านับใหม่ทุกสาย"
                : "ลำดับในไฟล์: ทีละ shipment → ใบคุมสรุป → ใบคุมรายร้าน → INV"}
            </Typography>
          </Card>

          <Card sx={{ ...CARD_SX, p: 1.75, flex: "2 1 420px", minWidth: 0 }} data-testid="bdc-doc-plan">
            <Typography fontWeight={600} mb={0.75}>
              ในไฟล์จะมี
            </Typography>
            {plan.length === 0 ? (
              <Typography className="sub">ยังไม่ได้เลือก shipment</Typography>
            ) : (
              <table className="bdc-t">
                <thead>
                  <tr>
                    <th>สาขา</th>
                    <th>Shipment</th>
                    <th>เอกสาร</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.map((entry) => (
                    <tr key={entry.shipment.shipment_uuid}>
                      <td>
                        <BranchChip branches={branches} companyId={entry.shipment.company_id} branchCode={entry.shipment.branch_code} />
                      </td>
                      <td>
                        <span className="dc">{entry.shipment.dc_code}</span> {roundLabel(entry.shipment.kind, entry.shipment.round)}{" "}
                        <span className="sub mono">{entry.shipment.po_number}</span>
                        {entry.shipment.is_test_mode && (
                          <>
                            {" "}
                            <Tag tone="warn">ทดสอบ</Tag>
                          </>
                        )}
                      </td>
                      <td>
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                          {entry.included.map((type) => (
                            <Tag key={type} tone="ok">
                              {documentLabel(type)}
                            </Tag>
                          ))}
                          {entry.skipped.map(({ type, reason }) => (
                            <Tag key={type} tone="mut">
                              ข้าม {documentLabel(type)} · {reason}
                            </Tag>
                          ))}
                        </Stack>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {plan.some((e) => e.skipped.length > 0) && (
              <Alert severity="info" sx={{ mt: 1 }}>
                เอกสารที่ shipment ยังไม่มีจะถูกข้าม ไม่ทำให้ไฟล์ทั้งชุดล้ม
              </Alert>
            )}
          </Card>
        </Stack>
      </Stack>
    </Box>
  );
}
